import isNull from "lodash/isNull";
import { useMutation } from "@tanstack/react-query";
import { useStripe } from "@stripe/react-stripe-js";
import { memo, useEffect } from "react";

import useStripePaymentMethodStore from "../../../../../components/WLStripePaymentMethods/store";
import useWLPromoStore from "../../../../../components/WLPromoField/store";
import { bugNotif } from "../../../../../api/bugsnag";
import { retryPayment } from "../../../../../api/stripe";
import { resolveOfferViewedAtFromSearchParams } from "../../../../../utils/resolveOfferViewedAtFromSearchParams";
import { getErrorMsg } from "../../../../../../Common/utils/common";
import { useFormsStore } from "../../../../../../Common/store/common";
import { usePurchaseV2Store } from "../../store";
import { useWebUserStore } from "../../../../../CommonStores/user/users";
import Stripe from "stripe";
import useSuccessRetryPaymentListenerStore from "../../SuccessPaymentListeners/SuccessRetryPaymentListener/store";

const isRetryPaymentAlreadyPaidError = (message: string): boolean => {
  const m = message.toLowerCase();
  return (
    m.includes("already paid") ||
    m.includes("already been paid") ||
    m.includes("invoice_already_paid") ||
    m.includes("cannot pay a paid")
  );
};

const RetryPaymentWCard = () => {
  const stripe = useStripe();

  const { setCreateAndRetryPaymentInvoiceFunc, setError } =
    usePurchaseV2Store();

  const { setFormLoading } = useFormsStore();

  const { setInProcessRetryPayment, setShowPaymentErrorAlert } =
    useSuccessRetryPaymentListenerStore();

  const { mutate: onRetryPayment } = useMutation({
    mutationFn: retryPayment,
    onMutate: () => {
      setFormLoading(true);
      setInProcessRetryPayment(true);
      setShowPaymentErrorAlert(false);
    },
    onSuccess: (response) => {
      console.log("got retryPayment response", response);
    },
    onError: (err: unknown) => {
      setFormLoading(false);
      const { rawErrorMsg } = getErrorMsg(err);
      console.log("got error", err);
      if (isRetryPaymentAlreadyPaidError(rawErrorMsg)) {
        setShowPaymentErrorAlert(false);
        useSuccessRetryPaymentListenerStore
          .getState()
          .setSuccessfulPurchase(true);
        return;
      }
      setShowPaymentErrorAlert(true);
      bugNotif(
        "<Stripe Subscription - createSubscriptionExistingUser>",
        rawErrorMsg,
      );
    },
  });

  const purchase = async () => {
    const { user } = useWebUserStore.getState();

    const { validPromoCode, validStripeCoupon } = useWLPromoStore.getState();

    const { selectedPaymentMethod } = useStripePaymentMethodStore.getState();

    const existingSubscription =
      useStripePaymentMethodStore.getState().existingSubscription;
    const searchParams = new URLSearchParams(window.location.search);
    const offerViewedAt =
      resolveOfferViewedAtFromSearchParams(searchParams);
    try {
      if (user === undefined || isNull(user) || user.stripeId === undefined) {
        return setError("No user found. Please re-open app and login.");
      }

      if (!existingSubscription) {
        return setError("No existing subscription found. Please re-open app.");
      }

      if (
        selectedPaymentMethod === undefined ||
        isNull(selectedPaymentMethod)
      ) {
        return setError("Please select a payment method.");
      }

      setFormLoading(true);

      console.log("will pass to retry payment --- ", {
        customerId: user.stripeId,
        paymentMethodId: selectedPaymentMethod.id,
        invoiceId: (existingSubscription.latest_invoice as Stripe.Invoice).id,
        subscriptionId: existingSubscription.id,
        couponId:
          validStripeCoupon !== undefined && !isNull(validStripeCoupon)
            ? validStripeCoupon.id
            : "",
        promoCode: validPromoCode || "",
        offerViewedAt,
      });

      onRetryPayment({
        customerId: user.stripeId,
        paymentMethodId: selectedPaymentMethod.id,
        invoiceId: (existingSubscription.latest_invoice as Stripe.Invoice).id,
        subscriptionId: existingSubscription.id,
        couponId:
          validStripeCoupon !== undefined && !isNull(validStripeCoupon)
            ? validStripeCoupon.id
            : "",
        promoCode: validPromoCode || "",
        offerViewedAt,
      });
    } catch (error) {
      setFormLoading(false);
      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      setError(errorMsg);
      bugNotif("<Stripe Retry Payment>", rawErrorMsg);
    }
  };

  useEffect(() => {
    if (!!stripe) {
      setCreateAndRetryPaymentInvoiceFunc(purchase);
    }
  }, [stripe]);

  return <></>;
};
export default memo(RetryPaymentWCard);
